import React from 'react';
import { colors, borderRadius, fontSize, fontWeight } from '../../constants/theme';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'error' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  dot?: boolean;
  count?: number;
  maxCount?: number;
  style?: React.CSSProperties;
  className?: string;
}

const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  count,
  maxCount = 99,
  style,
  className = ''
}) => {
  const variantStyles = {
    default: {
      backgroundColor: colors.ui.bgSecondary,
      color: colors.text.primary,
      border: `1px solid ${colors.ui.border}`
    },
    primary: {
      backgroundColor: colors.accent.blue,
      color: colors.ui.bg,
      border: 'none'
    },
    success: {
      backgroundColor: colors.ui.success,
      color: colors.ui.bg,
      border: 'none'
    },
    warning: {
      backgroundColor: '#FFA500',
      color: colors.ui.bg,
      border: 'none'
    },
    danger: {
      backgroundColor: colors.accent.red,
      color: colors.ui.bg,
      border: 'none'
    },
    error: {
      backgroundColor: colors.status?.error || colors.accent.red,
      color: colors.ui.bg,
      border: 'none'
    },
    secondary: {
      backgroundColor: colors.ui.bgSecondary,
      color: colors.text.secondary,
      border: `1px solid ${colors.ui.border}`
    },
    info: {
      backgroundColor: colors.accent.purple,
      color: colors.ui.bg,
      border: 'none'
    }
  };

  const sizeStyles = {
    sm: {
      padding: dot ? '0' : '2px 6px',
      fontSize: fontSize.xs,
      minWidth: dot ? '8px' : '18px',
      height: dot ? '8px' : 'auto'
    },
    md: {
      padding: dot ? '0' : '4px 8px',
      fontSize: fontSize.sm,
      minWidth: dot ? '10px' : '20px',
      height: dot ? '10px' : 'auto'
    },
    lg: {
      padding: dot ? '0' : '6px 12px',
      fontSize: fontSize.base,
      minWidth: dot ? '12px' : '24px',
      height: dot ? '12px' : 'auto'
    }
  };

  const badgeStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: dot ? borderRadius.full : borderRadius.full,
    fontWeight: fontWeight.medium,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    ...(variantStyles[variant] || variantStyles.default),
    ...(sizeStyles[size] || sizeStyles.md),
    ...style
  };

  const displayContent = () => {
    if (dot) {
      return null;
    }
    if (count !== undefined) {
      return count > maxCount ? `${maxCount}+` : count;
    }
    return children;
  };

  return (
    <span
      className={className}
      style={badgeStyles}
      role="status"
      aria-label={dot ? 'Status indicator' : undefined}
    >
      {displayContent()}
    </span>
  );
};

export default Badge;
export { Badge };